/**
 * Client-side screenshot capture. `capture()` rasterizes the current page to a PNG `Blob`,
 * with every element matching `redactSelectors` masked out *before* rasterization so
 * sensitive content never enters the captured image — as opposed to manual, after-the-fact
 * redaction (drawing boxes over the already-captured image; see `./redact.ts` and
 * `../ui/ScreenshotPreview.tsx`).
 *
 * This module intentionally never imports `@zumer/snapdom` at the top level. snapdom performs
 * real canvas rasterization, which jsdom (this package's Vitest environment) does not
 * implement, and importing a rasterization-heavy library at module-eval time is exactly the
 * kind of thing that can throw for reasons unrelated to whatever's under test. Deferring the
 * import into `SnapdomCapturer.capture()` means merely *constructing* a `SnapdomCapturer` —
 * which is what the controller's default wiring does on every `init()`, including under
 * jsdom in `bootstrap.test.ts` — is always safe. Unit tests exercise the `ScreenshotCapturer`
 * interface via a hand-rolled fake instead of this class; the real rasterization path is only
 * exercised in a real browser, by Playwright (Task 10).
 */
export interface ScreenshotCaptureOptions {
  /**
   * CSS selectors (from `data-feedback-redact` / `init({ redactSelectors })`) to mask before
   * rasterizing. Elements matching any of these never appear in the captured image.
   */
  redactSelectors?: string[];
}

export interface ScreenshotCapturer {
  capture(opts: ScreenshotCaptureOptions): Promise<Blob>;
}

/** One redaction overlay created by `maskElements`, tracked so `restoreElements` can undo it. */
interface MaskedElement {
  overlay: HTMLDivElement;
}

/**
 * Covers every element matching any of `selectors` with an opaque black `position: fixed`
 * overlay sized to its current viewport rect, appended to `document.body` — rather than
 * mutating the matched element's own styles — so the original element's layout/computed style
 * is never touched (nothing to accidentally leave in a bad state if capture throws partway
 * through) and nested/complex content is blacked out uniformly regardless of what's inside it.
 * Invalid selectors are skipped rather than thrown: the selector list comes from a page's own
 * `data-feedback-redact` attribute (or `init()` options) — untrusted input a typo in which
 * must never break screenshot capture entirely.
 */
function maskElements(selectors: string[]): MaskedElement[] {
  const masked: MaskedElement[] = [];
  for (const selector of selectors) {
    let matches: Element[];
    try {
      matches = Array.from(document.querySelectorAll(selector));
    } catch {
      continue;
    }
    for (const el of matches) {
      const rect = el.getBoundingClientRect();
      const overlay = document.createElement('div');
      overlay.setAttribute('data-feedback-redact-mask', 'true');
      Object.assign(overlay.style, {
        position: 'fixed',
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        background: '#000000',
        zIndex: '2147483647',
        pointerEvents: 'none',
      });
      document.body.appendChild(overlay);
      masked.push({ overlay });
    }
  }
  return masked;
}

/** Undoes `maskElements` — removes every overlay it created. */
function restoreElements(masked: MaskedElement[]): void {
  for (const { overlay } of masked) {
    overlay.remove();
  }
}

/**
 * Real capturer used in production, backed by `@zumer/snapdom`. Masks `redactSelectors` (see
 * `maskElements`) before rasterizing `document.documentElement` to a PNG blob, and always
 * restores the masks afterward (`finally`) — even if rasterization itself throws — so a failed
 * capture never leaves black overlays sitting on the host page.
 */
export class SnapdomCapturer implements ScreenshotCapturer {
  async capture({ redactSelectors = [] }: ScreenshotCaptureOptions): Promise<Blob> {
    if (typeof document === 'undefined') {
      throw new Error('SnapdomCapturer requires a DOM environment.');
    }

    const masked = maskElements(redactSelectors);
    try {
      const { snapdom } = await import('@zumer/snapdom');
      // The whole widget stays out of the picture: its shadow host carries
      // `data-ft-host` (`mount.ts`), and removing it from the clone removes the
      // open modal, its scrim and the launcher with it. That is what lets the
      // modal open before the raster runs (I22657) instead of hiding itself.
      return await snapdom.toBlob(document.documentElement, {
        type: 'png',
        exclude: ['[data-ft-host]'],
        excludeMode: 'remove',
      });
    } finally {
      restoreElements(masked);
    }
  }
}
